import * as React from "react"
import { useTranslation } from "react-i18next"
import { ArchivedChatContext } from "@/contexts/ArchivedChatContext"
import { cn } from "@/lib/utils"
import { MessageContent, useMessageContext } from "./message"
import { Download, Loader2, Share2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { isMacOs, isMobile, isWindows } from "@/utils/detectDevice"
import { updateImageGallery } from "@/reducers/overlaysSlice"
import { mobileRoutes, setMobileRoute } from "@/routes/routes"
import { useAppDispatch } from "@/reducers/hooks"
import useDownloadFileName from "@/hooks/useDownloadFileName"
import useHandleDownload from "@/hooks/useHandleDownload"
import useHandleShareLink from "@/hooks/useHandleShareLink"
import CMRMetaData from "@/components/cmr/cmr-meta-data"
import useCMRMetadata from "@/hooks/useCMRMetadata"
import useMessage from "@/components/ui/message/useMessage"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"

export interface MessageImageProps
  extends React.HTMLAttributes<HTMLDivElement> {
  src?: string
  alt?: string
  showDownload?: boolean
  showShare?: boolean
  onDownload?: () => void
  onShare?: () => void
  onExternalView?: () => void
  loading?: boolean
  width?: number | string
  height?: number | string
  aspectRatio?: "square" | "video" | "auto"
  forwarded?: boolean
  reply?: boolean
}

const MessageImage = React.forwardRef<HTMLDivElement, MessageImageProps>(
  (
    {
      className,
      src,
      alt,
      showDownload = true,
      showShare = true,
      onDownload,
      onShare,
      onExternalView,
      loading = false,
      width,
      height,
      aspectRatio = "auto",
      children,
      forwarded = false,
      reply = false,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { actionButtonsVisible } = useMessageContext()

    const message = useMessage({ forwarded, reply })
    const archivedChat = React.useContext(ArchivedChatContext)

    const url = src ? src : mapFileUrlToSameOrigin(message?.url || "")
    const downloadFileName = useDownloadFileName(message)
    const download = useHandleDownload()
    const share = useHandleShareLink(message)

    const handleDownload = onDownload
      ? onDownload
      : downloadFileName && url
        ? (e: React.MouseEvent) => {
            e.stopPropagation()
            download(url, downloadFileName)
          }
        : undefined
    const handleShare = onShare
      ? onShare
      : downloadFileName
        ? (e: React.MouseEvent) => {
            e.stopPropagation()
            share(downloadFileName)
          }
        : undefined

    const { documentMetadata, driverContext, hasCMRMetadata } =
      useCMRMetadata(message)

    const [imageLoading, setImageLoading] = React.useState(true)
    const [imageError, setImageError] = React.useState(false)

    const handleImageLoad = () => {
      setImageLoading(false)
      setImageError(false)
    }

    const handleImageError = () => {
      setImageLoading(false)
      setImageError(true)
    }

    const handleExternalView = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onExternalView) {
        onExternalView()
      } else {
        window.open(url, "_blank")
      }
    }

    const handleOnClick = (e: React.MouseEvent) => {
      const isCtrlOrCmdClick =
        e.button === 0 && ((isWindows && e.ctrlKey) || (isMacOs && e.metaKey))
      const isMiddleClick = e.button === 1

      if (isCtrlOrCmdClick || isMiddleClick) {
        window.open(url)
      } else {
        dispatch(
          updateImageGallery({
            visible: true,
            initialImage: url,
            externalSource: archivedChat?.mediaSource,
          }),
        )
        if (isMobile && !archivedChat) {
          setMobileRoute(mobileRoutes.GALLERY)
        }
      }
    }

    const getAspectRatioClass = () => {
      switch (aspectRatio) {
        case "square":
          return "aspect-square"
        case "video":
          return "aspect-video"
        default:
          return ""
      }
    }

    const imageElement = (
      <div
        className={cn(
          "group/image relative overflow-hidden rounded-lg bg-muted",
          // Mobile-first responsive sizing
          "max-w-56", // Mobile default
          "sm:max-w-64", // Larger on small screens and up
          // Shrink when action buttons are visible on mobile
          isMobile && actionButtonsVisible && "max-w-48",
          getAspectRatioClass(),
          className,
        )}
        style={{ width, height }}
      >
        {(loading || imageLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {imageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
            <div className="text-center">
              <div className="text-sm">{t("chat.media.imageLoadFailed")}</div>
              <Button
                variant="link"
                size="sm"
                onClick={handleExternalView}
                className="h-auto p-0 text-xs"
              >
                {t("chat.media.tryOpeningExternally")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <img
              src={url}
              alt={alt ?? t("chat.media.imageAlt")}
              className={cn(
                "h-full w-full object-cover transition-opacity",
                imageLoading && "opacity-0",
              )}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          </>
        )}

        {/* Hover overlay with actions */}
        {!imageError && !loading && !imageLoading && (
          <div
            data-testid="image-element"
            className="absolute inset-0 cursor-pointer bg-black/0 transition-colors group-hover/image:bg-black/20"
            onClick={handleOnClick}
          >
            <div
              className={cn(
                "absolute bottom-2 right-2 flex gap-2 opacity-0 transition-opacity group-hover/image:opacity-100",
                // More compact spacing when action buttons are visible on mobile
                isMobile && actionButtonsVisible && "gap-1",
                isMobile
                  ? "opacity-100"
                  : "opacity-0 group-hover/image:opacity-100",
              )}
            >
              {showShare && handleShare && (
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-9 w-9 bg-background dark:bg-input/30",
                    // Smaller buttons when action buttons are visible on mobile
                    isMobile && actionButtonsVisible && "h-7 w-7",
                  )}
                  onClick={handleShare}
                >
                  <Share2
                    className={cn(
                      "h-4 w-4",
                      isMobile && actionButtonsVisible && "h-3 w-3",
                    )}
                  />
                </Button>
              )}
              {showDownload && handleDownload && (
                <>
                  {isMobile ? (
                    <a
                      href={url}
                      onClick={(e) => e.stopPropagation()}
                      download
                      className={cn(
                        "inline-flex h-9 w-9 items-center justify-center rounded-md bg-background hover:bg-accent hover:text-accent-foreground dark:bg-input/30",
                        // Smaller buttons when action buttons are visible on mobile
                        actionButtonsVisible && "h-7 w-7",
                      )}
                    >
                      <Download
                        className={cn(
                          "h-4 w-4",
                          actionButtonsVisible && "h-3 w-3",
                        )}
                      />
                    </a>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-9 w-9 bg-background dark:bg-input/30",
                        // Smaller buttons when action buttons are visible on mobile
                        isMobile && actionButtonsVisible && "h-7 w-7",
                      )}
                      onClick={handleDownload}
                    >
                      <Download
                        className={cn(
                          "h-4 w-4",
                          isMobile && actionButtonsVisible && "h-3 w-3",
                        )}
                      />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )

    return (
      <MessageContent ref={ref} {...props}>
        <div
          className={cn(
            "flex items-start",
            isMobile ? "flex-col-reverse gap-4" : "gap-2",
            // More compact gap when action buttons are visible on mobile
            isMobile && actionButtonsVisible && "gap-2",
          )}
        >
          {hasCMRMetadata && (
            <div className="flex-shrink-0">
              <CMRMetaData
                message={message}
                metadata={documentMetadata?.data}
                driverContext={driverContext}
                documentType={documentMetadata?.documentType}
                blurry={documentMetadata?.blurry}
                remarks={documentMetadata?.remarks}
              />
            </div>
          )}
          {imageElement}
        </div>
        {children}
      </MessageContent>
    )
  },
)

MessageImage.displayName = "MessageImage"

export { MessageImage }
